/* ==========================================================================
   NUPEP NAD+ 1000 MG — BUY 2 + GET 1 FREE  (theme-side helper)

   Shopify's discount engine enforces the free unit. Create the code
   NAD2GET1FREE as a "Buy X get Y" discount:
     Buy 2 x NAD+ 1000 MG  ->  get 1 x NAD+ 1000 MG at 100% off
     No limit on uses per order (6 units -> 2 free, 9 -> 3 free ...)

   This script:
   1. Auto-adds the free unit(s) when a qualifying quantity is added to the
      cart (product page, quick add, bundle add - anything hitting /cart/add).
   2. Keeps the NAD2GET1FREE code applied once the cart holds a full set.
   3. Drives the dynamic quantity messaging (product panel + cart strip).
   Prices are never changed in JavaScript.
   ========================================================================== */
(function (window, document) {
  'use strict';
  if (window.__nupepNadPromoLoaded) return;
  window.__nupepNadPromoLoaded = true;

  const CFG = Object.assign(
    { code: 'NAD2GET1FREE', buy: 2, free: 1, handle: '', exclusive: true, productLabel: 'NAD+ 1000 MG' },
    window.NUPEP_NAD_PROMO || {}
  );
  if (!window.NUPEP_NAD_PROMO) return; // promo disabled in theme settings

  const SET = CFG.buy + CFG.free; // units per qualifying set (3)
  const nativeFetch = window.fetch.bind(window);
  const variantIds = new Set();
  let lastCart = null;
  let applying = false;
  let lastApplyAttempt = 0;

  /* ------------------------------ maths ------------------------------ */
  const freeIn = (units) => Math.floor(units / SET) * CFG.free;           // free units Shopify will discount
  const paidIn = (units) => units - freeIn(units);                         // units the customer pays for
  const freeFor = (paid) => Math.floor(paid / CFG.buy) * CFG.free;         // free units earned by paid units
  // Units to add when `qty` paid units are added to a cart already holding `cartUnits`
  function unitsToAdd(qty, cartUnits) {
    const paidAfter = paidIn(cartUnits) + qty;
    const targetTotal = paidAfter + freeFor(paidAfter);
    return Math.max(qty, targetTotal - cartUnits);
  }

  /* --------------------------- identification --------------------------- */
  function collectVariantIds() {
    document.querySelectorAll('[data-nupep-nad-variant-ids]').forEach((el) => {
      String(el.getAttribute('data-nupep-nad-variant-ids') || '')
        .split(',').map((v) => v.trim()).filter(Boolean).forEach((v) => variantIds.add(v));
    });
  }
  function isNadVariant(id) { return variantIds.has(String(id)); }
  function isNadItem(item) {
    if (isNadVariant(item?.variant_id) || isNadVariant(item?.id)) return true;
    const handle = String(item?.handle || '').toLowerCase();
    if (CFG.handle) return handle === String(CFG.handle).toLowerCase();
    const hay = `${handle} ${String(item?.product_title || '').toLowerCase()}`;
    return hay.includes('nad') && hay.includes('1000');
  }
  const nadUnits = (cart) => (cart?.items || []).reduce((t, i) => t + (isNadItem(i) ? Number(i.quantity || 0) : 0), 0);

  /* ------------------------------- cart ------------------------------- */
  async function getCart() {
    const res = await nativeFetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('cart read failed');
    lastCart = await res.json();
    return lastCart;
  }

  /* ------------------ auto-add the free unit (fetch hook) ------------------ */
  const ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  function bodyHasNad(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return isNadVariant(body.get('id'));
    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        return Array.isArray(data.items) ? data.items.some((l) => isNadVariant(l && l.id)) : isNadVariant(data.id);
      } catch (e) { return false; }
    }
    return false;
  }

  async function adjustBody(body) {
    if (!body) return null;
    collectVariantIds();
    if (!variantIds.size || !bodyHasNad(body)) return null; // other products: untouched, no extra request

    let cart;
    try { cart = await getCart(); } catch (e) { return null; }
    let units = nadUnits(cart);

    // FormData: id + quantity
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const qty = Math.max(1, parseInt(body.get('quantity') || '1', 10));
      const copy = new FormData();
      for (const [k, v] of body.entries()) copy.append(k, v);
      copy.set('quantity', String(unitsToAdd(qty, units)));
      return copy;
    }

    // JSON string: { id, quantity } or { items: [...] }
    const data = JSON.parse(body);
    const bump = (line) => {
      if (!line || !isNadVariant(line.id)) return;
      const qty = Math.max(1, parseInt(line.quantity || 1, 10));
      const total = unitsToAdd(qty, units);
      line.quantity = total;
      units += total;
    };
    if (Array.isArray(data.items)) data.items.forEach(bump); else bump(data);
    return JSON.stringify(data);
  }

  window.fetch = async function (input, init) {
    let isAdd = false;
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && ADD_RE.test(url)) {
        isAdd = true;
        const nextBody = await adjustBody(init && init.body);
        if (nextBody) init = Object.assign({}, init, { body: nextBody });
      }
    } catch (e) {
      console.warn('[NUPEP NAD+ Offer] Could not adjust add-to-cart request.', e);
    }
    const response = await nativeFetch(input, init);
    if (isAdd && response.ok) setTimeout(() => sync(), 150);
    return response;
  };

  /* --------------------------- discount code --------------------------- */
  async function ensureDiscount(cart) {
    if (applying || nadUnits(cart) < SET) return cart;
    const codes = (cart.discount_codes || [])
      .filter((d) => d.applicable !== false)
      .map((d) => String(d.code || '').toUpperCase());
    const hasDeal = codes.includes(CFG.code);
    const hasOthers = codes.some((c) => c !== CFG.code);

    // Already clean: only the deal code is applied.
    if (hasDeal && !hasOthers) return cart;
    // Not exclusive: leave customer codes alone, but still add ours when none is present.
    if (!CFG.exclusive && codes.length) return cart;
    if (Date.now() - lastApplyAttempt < 15000) return cart;

    applying = true;
    lastApplyAttempt = Date.now();
    try {
      // Applying the deal code replaces any other code held in the session.
      await nativeFetch(`/discount/${encodeURIComponent(CFG.code)}?redirect=${encodeURIComponent('/cart.js')}`, { credentials: 'same-origin' });
      return await getCart();
    } catch (e) {
      console.warn('[NUPEP NAD+ Offer] Discount code could not be applied automatically.', e);
      return cart;
    } finally {
      applying = false;
    }
  }

  /* ------------------------------ messaging ------------------------------ */
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

  function renderCart(cart) {
    const units = nadUnits(cart);
    const codes = (cart?.discount_codes || []).map((d) => String(d.code || '').toUpperCase());
    const codeBlocked = units >= SET && codes.some((c) => c !== CFG.code);
    document.querySelectorAll('[data-nupep-nad-cart-offer]').forEach((el) => {
      if (!units) { if (!el.hidden) el.hidden = true; return; }
      if (el.hidden) el.hidden = false;
      const title = el.querySelector('[data-nupep-nad-cart-offer-title]');
      const text = el.querySelector('[data-nupep-nad-cart-offer-text]');
      const free = freeIn(units);
      const rem = units % SET;
      let t; let m;
      if (free) {
        t = 'NAD+ 1000 MG OFFER UNLOCKED';
        m = `${plural(free, 'free unit')} included with your ${units} ${CFG.productLabel}.`;
        if (rem === 2) m += ' Add 1 more and it is free too.';
        if (codeBlocked) m += ` This offer cannot be combined with other discount codes; use ${CFG.code} to claim it.`;
      } else {
        t = 'NAD+ 1000 MG SPECIAL OFFER';
        m = rem === 2 ? 'Add 1 more NAD+ 1000 MG and it is FREE.' : 'Buy 2 NAD+ 1000 MG and get 1 FREE.';
      }
      if (title && title.textContent !== t) title.textContent = t;
      if (text && text.textContent !== m) text.textContent = m;
    });
  }

  function qtyInput() {
    return document.querySelector(
      '#product-form input[name="quantity"], product-info input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]'
    );
  }

  function renderPanel() {
    const panels = document.querySelectorAll('[data-nupep-nad-panel]');
    if (!panels.length) return;
    const input = qtyInput();
    const qty = Math.max(1, parseInt(input?.value || '1', 10) || 1);
    const cartUnits = nadUnits(lastCart);
    const paidAfter = paidIn(cartUnits) + qty;
    const earnedTotal = freeFor(paidAfter);
    const addNow = unitsToAdd(qty, cartUnits);
    const freeNow = addNow - qty;
    const odd = paidAfter % CFG.buy !== 0;

    let status; let summary = '';
    let state = 'locked';
    if (earnedTotal < 1) {
      status = `Add ${CFG.buy - paidAfter} more to unlock your FREE unit.`;
      summary = '';
    } else {
      state = 'unlocked';
      status = freeNow > 0
        ? `Offer unlocked — ${plural(freeNow, 'FREE unit')} will be added automatically.`
        : 'Offer unlocked — your free units are already in your cart.';
      summary = `Adding ${plural(addNow, 'unit')} to your cart: ${plural(qty, 'paid unit')}${freeNow > 0 ? ` + ${plural(freeNow, 'FREE unit')}` : ''}.`;
      if (odd) summary += ` Add ${CFG.buy - (paidAfter % CFG.buy)} more to earn another free unit.`;
    }

    panels.forEach((panel) => {
      if (panel.dataset.state !== state) panel.dataset.state = state;
      const s = panel.querySelector('[data-nupep-nad-status]');
      const m = panel.querySelector('[data-nupep-nad-summary]');
      if (s && s.textContent !== status) s.textContent = status;
      if (m) { if (m.textContent !== summary) m.textContent = summary; if (m.hidden === !!summary) m.hidden = !summary; }
      const on = { 1: paidAfter >= 1, 2: paidAfter >= 2, free: earnedTotal >= 1 };
      panel.querySelectorAll('[data-nupep-nad-step]').forEach((li) => {
        const want = !!on[li.getAttribute('data-nupep-nad-step')];
        if (li.classList.contains('is-on') !== want) li.classList.toggle('is-on', want);
      });
    });
  }

  function bindPanel() {
    if (!document.querySelector('[data-nupep-nad-panel]')) return;
    const update = () => renderPanel();
    ['input', 'change', 'keyup'].forEach((evt) => document.addEventListener(evt, (e) => {
      if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) update();
    }));
    // +/- steppers change the value after the click
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('quantity-input, .quantity, .elite-cart-row, [name="plus"], [name="minus"]')) setTimeout(update, 60);
    });
    document.addEventListener('nupep:quantity:change', update);
    update();
  }

  /* ------------------------------- sync ------------------------------- */
  async function sync(cartFromEvent) {
    try {
      let cart = cartFromEvent && Array.isArray(cartFromEvent.items) ? cartFromEvent : await getCart();
      lastCart = cart;
      cart = await ensureDiscount(cart);
      lastCart = cart;
      renderCart(cart);
      renderPanel();
    } catch (e) { /* silent */ }
  }

  function boot() {
    collectVariantIds();
    bindPanel();
    sync();

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => sync(event?.cartData));
    }
    document.addEventListener('nupep:cart-rendered', (event) => sync(event?.detail));

    // Cart drawer / cart page re-render their HTML: re-paint our strip when that happens
    let timer = null;
    new MutationObserver((mutations) => {
      const external = mutations.some((mu) => !(mu.target.closest && mu.target.closest('[data-nupep-nad-cart-offer], [data-nupep-nad-panel]')));
      if (!external) return;
      clearTimeout(timer);
      timer = setTimeout(() => { collectVariantIds(); if (lastCart) { renderCart(lastCart); renderPanel(); } }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})(window, document);
